/* Zenfusion Flow — shared components. No dependencies. */

/* <zf-slider data-autoplay="5000" data-loop>
     <ul class="zf-slider__track">...<li>slide</li>...</ul>
     <div class="zf-slider__nav"> [data-prev] [data-dots] [data-next] </div>
   </zf-slider> */
if (!customElements.get('zf-slider')) {
  class ZfSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.zf-slider__track');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.dotsWrap = this.querySelector('[data-dots]');
      this.loop = this.hasAttribute('data-loop');
      this.prev && this.prev.addEventListener('click', () => this.go(-1));
      this.next && this.next.addEventListener('click', () => this.go(1));
      this.buildDots();
      this.onScroll = this.update.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.resizeObserver = new ResizeObserver(() => { this.buildDots(); this.update(); });
      this.resizeObserver.observe(this.track);
      const delay = parseInt(this.dataset.autoplay, 10);
      if (delay > 0 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.timer = setInterval(() => { if (!this.paused) this.go(1, true); }, delay);
        ['mouseenter', 'focusin', 'touchstart'].forEach((e) => this.addEventListener(e, () => (this.paused = true), { passive: true }));
        ['mouseleave', 'focusout'].forEach((e) => this.addEventListener(e, () => (this.paused = false)));
      }
      this.update();
    }
    disconnectedCallback() {
      clearInterval(this.timer);
      this.resizeObserver && this.resizeObserver.disconnect();
    }
    get step() {
      if (this.slides.length < 2) return this.track.clientWidth;
      return this.slides[1].offsetLeft - this.slides[0].offsetLeft;
    }
    get perView() {
      return Math.max(1, Math.round(this.track.clientWidth / this.step));
    }
    get pages() {
      return Math.max(1, this.slides.length - this.perView + 1);
    }
    get index() {
      return Math.round(this.track.scrollLeft / this.step);
    }
    go(dir, auto) {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (dir > 0 && this.track.scrollLeft >= max) {
        if (this.loop || auto) this.track.scrollTo({ left: 0 });
        return;
      }
      if (dir < 0 && this.track.scrollLeft <= 2) {
        if (this.loop) this.track.scrollTo({ left: this.track.scrollWidth });
        return;
      }
      this.track.scrollBy({ left: dir * this.step });
    }
    goTo(i) {
      this.track.scrollTo({ left: i * this.step });
    }
    buildDots() {
      if (!this.dotsWrap) return;
      const n = this.pages;
      if (this.dotsWrap.childElementCount === n) return;
      this.dotsWrap.innerHTML = '';
      if (n < 2) return;
      for (let i = 0; i < n; i++) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'zf-slider__dot';
        b.setAttribute('aria-label', `Go to slide ${i + 1}`);
        b.addEventListener('click', () => this.goTo(i));
        this.dotsWrap.appendChild(b);
      }
    }
    update() {
      const i = this.index;
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (!this.loop) {
        this.prev && (this.prev.disabled = this.track.scrollLeft <= 2);
        this.next && (this.next.disabled = this.track.scrollLeft >= max);
      }
      if (this.dotsWrap) Array.from(this.dotsWrap.children).forEach((d, n) => d.setAttribute('aria-current', n === i));
      const hideNav = this.track.scrollWidth <= this.track.clientWidth + 2;
      const nav = this.querySelector('.zf-slider__nav');
      nav && (nav.hidden = hideNav);
    }
  }
  customElements.define('zf-slider', ZfSlider);
}

/* <zf-video> — click-to-load YouTube/Vimeo/video. Template child holds the real embed. */
if (!customElements.get('zf-video')) {
  class ZfVideo extends HTMLElement {
    connectedCallback() {
      const btn = this.querySelector('[data-zf-play]');
      btn && btn.addEventListener('click', () => this.load(), { once: true });
    }
    load() {
      const tpl = this.querySelector('template');
      if (!tpl) return;
      const node = tpl.content.firstElementChild.cloneNode(true);
      this.querySelectorAll(':scope > :not(template)').forEach((el) => el.remove());
      this.appendChild(node);
      if (node.tagName === 'VIDEO') node.play && node.play();
    }
  }
  customElements.define('zf-video', ZfVideo);
}

/* <zf-countdown data-end="2026-12-31T23:59:59" data-evergreen-minutes="15" data-key="x">
   children: [data-d] [data-h] [data-m] [data-s]; optional [data-expired] */
if (!customElements.get('zf-countdown')) {
  class ZfCountdown extends HTMLElement {
    connectedCallback() {
      this.end = this.resolveEnd();
      if (!this.end) return;
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    resolveEnd() {
      const mins = parseInt(this.dataset.evergreenMinutes, 10);
      if (mins > 0) {
        const key = 'zf-cd-' + (this.dataset.key || 'default');
        let end;
        try { end = parseInt(sessionStorage.getItem(key), 10); } catch (e) {}
        if (!end || end < Date.now()) {
          end = Date.now() + mins * 60000;
          try { sessionStorage.setItem(key, end); } catch (e) {}
        }
        return end;
      }
      const t = Date.parse(this.dataset.end || '');
      return isNaN(t) ? null : t;
    }
    tick() {
      let diff = Math.max(0, this.end - Date.now());
      const pad = (n) => String(n).padStart(2, '0');
      const d = Math.floor(diff / 864e5); diff -= d * 864e5;
      const h = Math.floor(diff / 36e5); diff -= h * 36e5;
      const m = Math.floor(diff / 6e4); diff -= m * 6e4;
      const s = Math.floor(diff / 1e3);
      const set = (sel, v) => this.querySelectorAll(sel).forEach((el) => (el.textContent = v));
      set('[data-d]', pad(d)); set('[data-h]', pad(h)); set('[data-m]', pad(m)); set('[data-s]', pad(s));
      if (this.end - Date.now() <= 0) {
        clearInterval(this.timer);
        this.classList.add('is-expired');
        if (this.hasAttribute('data-hide-on-expire')) this.closest('[data-zf-countdown-wrap]')?.setAttribute('hidden', '');
      }
    }
  }
  customElements.define('zf-countdown', ZfCountdown);
}

/* <zf-counter data-to="100000" data-suffix="+"> animates a number when visible */
if (!customElements.get('zf-counter')) {
  class ZfCounter extends HTMLElement {
    connectedCallback() {
      const to = parseFloat(this.dataset.to);
      if (isNaN(to)) return;
      const fmt = (n) => (this.dataset.prefix || '') + Math.round(n).toLocaleString() + (this.dataset.suffix || '');
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { this.textContent = fmt(to); return; }
      this.textContent = fmt(0);
      const io = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        const dur = parseInt(this.dataset.duration, 10) || 1600;
        const t0 = performance.now();
        const step = (t) => {
          const p = Math.min(1, (t - t0) / dur);
          this.textContent = fmt(to * (1 - Math.pow(1 - p, 3)));
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      });
      io.observe(this);
    }
  }
  customElements.define('zf-counter', ZfCounter);
}

/* [data-zf-modal-open="dialog-id"] opens a <dialog class="zf-modal" id="dialog-id"> */
document.addEventListener('click', (e) => {
  const opener = e.target.closest('[data-zf-modal-open]');
  if (opener) {
    const dlg = document.getElementById(opener.dataset.zfModalOpen);
    if (dlg && dlg.showModal) { e.preventDefault(); dlg.showModal(); }
    return;
  }
  const closer = e.target.closest('[data-zf-modal-close]');
  if (closer) { closer.closest('dialog')?.close(); return; }
  if (e.target.tagName === 'DIALOG' && e.target.classList.contains('zf-modal')) e.target.close();
});

/* Shared add-to-cart helper used by zf sections (bundle, add-ons, quick add).
   items: [{id, quantity, properties}] — refreshes Dawn cart drawer / bubble when present. */
window.zfAddToCart = async function (items, opener) {
  const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  const body = { items };
  if (cart && cart.getSectionsToRender) body.sections = cart.getSectionsToRender().map((s) => s.id);
  body.sections_url = window.location.pathname;
  const res = await fetch(window.routes.cart_add_url + '.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (data.status) throw new Error(data.description || data.message || 'Could not add to cart');
  if (cart && cart.renderContents) {
    if (!data.sections) data.sections = {};
    cart.setActiveElement && opener && cart.setActiveElement(opener);
    data.key = data.items && data.items[0] && data.items[0].key;
    try { cart.renderContents(data); } catch (err) { window.location.href = window.routes.cart_url; }
    cart.classList && cart.classList.contains('is-empty') && cart.classList.remove('is-empty');
  } else {
    window.location.href = window.routes.cart_url;
  }
  if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'zf', cartData: data });
  }
  return data;
};

/* zf-marquee fill: repeat a group's items until it is at least as wide as the track, so the loop never shows a gap */
(function () {
  function fill(m) {
    var groups = m.querySelectorAll(':scope > .zf-marquee__group');
    if (!groups.length || m.dataset.zfFilled) return;
    var g = groups[0], items = Array.prototype.slice.call(g.children), guard = 0, w0 = g.scrollWidth;
    if (!items.length || !w0) return;
    while (g.scrollWidth < m.clientWidth && guard++ < 12) {
      groups.forEach(function (grp) { items.forEach(function (it) { var c = it.cloneNode(true); c.setAttribute('aria-hidden', 'true'); grp.appendChild(c); }); });
    }
    if (g.scrollWidth > w0) {
      /* keep the same scrolling speed (px/s) after repeating items */
      var sp = parseFloat(getComputedStyle(m).getPropertyValue('--zf-marquee-speed')) || 30;
      m.style.setProperty('--zf-marquee-speed', (sp * g.scrollWidth / w0).toFixed(1) + 's');
    }
    m.dataset.zfFilled = '1';
  }
  function run() { document.querySelectorAll('.zf-marquee').forEach(fill); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
  document.addEventListener('shopify:section:load', function () { document.querySelectorAll('.zf-marquee').forEach(function (m) { delete m.dataset.zfFilled; fill(m); }); });
})();

/* Yeps top bar (push mode).
   --zf-yeps-h  : page offset = bar height, measured only at the top of the page (never changes while scrolling -> no content jump)
   --zf-yeps-top: where the sticky header sits = bottom edge of the bar right now (0 once the bar has scrolled/hidden away) */
(function () {
  var root = document.documentElement, tries = 0, closed = false;
  function bar() { var host = document.getElementById('yeps'); var sr = host && host.shadowRoot; return sr && sr.querySelector('.yeps-frame.yeps-top, .yeps-top'); }
  function visible(b) { return b && root.classList.contains('yeps-top-show') && getComputedStyle(b).display !== 'none'; }
  function pad() { return parseFloat(getComputedStyle(document.body).paddingTop) || 0; }
  function measureOffset() {
    var b = bar(); if (window.scrollY > 5 && root.style.getPropertyValue('--zf-yeps-h')) return;
    var reserved = root.classList.contains('zf-topbar-reserve');
    /* bar not on screen (yet): keep whatever space is reserved. Removing it now would shift the whole page (CLS);
       the next page view uses the remembered 0 instead. A visitor closing the bar is handled by the class observer (input-driven, no CLS). */
    if (!visible(b)) {
      if (reserved && !closed) return;
      root.style.setProperty('--zf-yeps-h', '0px'); remember(0); return;
    }
    var bottom = Math.round(b.getBoundingClientRect().bottom);
    /* where the header would sit without our padding (Yeps may push the page itself) */
    var g = document.querySelector('.shopify-section-group-header-group');
    var natural = g ? Math.round(g.getBoundingClientRect().top - pad()) : 0;
    var h = Math.max(0, bottom - natural);
    root.style.setProperty('--zf-yeps-h', h + 'px');
    remember(h);
  }
  function measureTop() {
    var b = bar(); var t = 0;
    if (visible(b)) t = Math.max(0, Math.round(b.getBoundingClientRect().bottom));
    if (root.style.getPropertyValue('--zf-yeps-top') !== t + 'px') root.style.setProperty('--zf-yeps-top', t + 'px');
  }
  var lastSaved = null;
  function remember(h) {
    if (h === lastSaved) return;
    lastSaved = h;
    try { localStorage.setItem('zfTopbarH' + (window.matchMedia('(max-width: 749px)').matches || screen.width < 750 ? 'm' : 'd'), String(h)); } catch (e) {}
    /* only touch the class when it is actually set: a no-op classList change still fires the class MutationObserver below (loop) */
    if (!h && root.classList.contains('zf-topbar-reserve')) root.classList.remove('zf-topbar-reserve');
  }
  function all() { measureOffset(); measureTop(); }
  function hook() {
    var b = bar();
    if (!b) { if (tries++ < 40) setTimeout(hook, 250); else { try { localStorage.setItem('zfTopbarH' + (matchMedia('(max-width: 749px)').matches || screen.width < 750 ? 'm' : 'd'), '0'); } catch (e) {} } return; }
    all();
    setTimeout(all, 8100);
    if (window.ResizeObserver) new ResizeObserver(all).observe(b);
    new MutationObserver(function (recs) { for (var i = 0; i < recs.length; i++) { if (recs[i].oldValue !== root.className) { if (/\byeps-top-show\b/.test(recs[i].oldValue || '') && !root.classList.contains('yeps-top-show')) closed = true; all(); return; } } }).observe(root, { attributes: true, attributeFilter: ['class'], attributeOldValue: true });
    window.addEventListener('resize', function () { root.style.removeProperty('--zf-yeps-h'); all(); });
    var tick = false; window.addEventListener('scroll', function () { if (tick) return; tick = true; requestAnimationFrame(function () { tick = false; measureTop(); }); }, { passive: true });
  }
  hook();
})();

/* Lazy raw videos (snippets/zf-media): load + autoplay only when the video comes near the screen. */
(function () {
  function wake(v) {
    [v].concat([].slice.call(v.querySelectorAll('source[data-zf-src]'))).forEach(function (el) {
      if (el.dataset.zfSrc) { el.src = el.dataset.zfSrc; el.removeAttribute('data-zf-src'); }
    });
    if (v.hasAttribute('data-zf-click')) return clickToPlay(v);
    v.load();
    if (v.hasAttribute('data-zf-autoplay')) { v.muted = true; var p = v.play(); if (p && p.catch) p.catch(function () {}); }
  }
  /* Theme settings > Zenfusion speed > "Videos: click to play": show the first frame + a play button; the video only downloads when tapped. */
  function clickToPlay(v) {
    if (v.dataset.zfClickReady) return; v.dataset.zfClickReady = '1';
    v.removeAttribute('autoplay'); v.preload = 'metadata';
    if (v.src && v.src.indexOf('#t=') < 0) v.src = v.src + '#t=0.1';
    else { var s = v.querySelector('source'); if (s && s.src.indexOf('#t=') < 0) s.src = s.src + '#t=0.1'; }
    v.load();
    var wrap = document.createElement('div'); wrap.className = 'zf-vclick';
    v.parentNode.insertBefore(wrap, v); wrap.appendChild(v);
    var b = document.createElement('button'); b.type = 'button'; b.className = 'zf-vclick__btn'; b.setAttribute('aria-label', 'Play video');
    b.innerHTML = '<svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true"><path d="M8 5v14l11-7z" fill="currentColor"/></svg>';
    wrap.appendChild(b);
    b.addEventListener('click', function () {
      wrap.classList.add('is-playing'); v.controls = true; v.muted = false;
      var p = v.play(); if (p && p.catch) p.catch(function () { v.muted = true; v.play(); });
    });
  }
  function init() {
    var vids = [].slice.call(document.querySelectorAll('video[data-zf-src], video[data-zf-autoplay], video[data-zf-click]'));
    document.querySelectorAll('source[data-zf-src]').forEach(function (s) { var v = s.closest('video'); if (v && vids.indexOf(v) < 0) vids.push(v); });
    if (!vids.length) return;
    if (!('IntersectionObserver' in window)) { vids.forEach(wake); return; }
    var io = new IntersectionObserver(function (es) { es.forEach(function (e) { if (e.isIntersecting) { io.unobserve(e.target); wake(e.target); } }); }, { rootMargin: '300px 0px' });
    vids.forEach(function (v) { if (!v.dataset.zfLazyBound) { v.dataset.zfLazyBound = '1'; io.observe(v); } });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();

/* Lazy video iframes (snippets/zf-lazy-iframes): YouTube/Vimeo players in custom HTML carry data-zf-src instead of src.
   Until the player loads, the frame shows a tiny srcdoc page: the video thumbnail and a real play link (tap / Enter starts the
   video right away). Players load (a) when they are on screen at page load — this also lets Googlebot, which renders with a
   very tall viewport, see every embed — and (b) after the first scroll / tap / key, once within 600px of the screen.
   Frames added later (quick-add modals, product swaps, section reloads) are picked up by a MutationObserver.
   A YouTube player costs ~800 KB and ~0.7 s of main-thread work per iframe. */
(function () {
  var io0, io1, interacted = false, SEL = 'iframe[data-zf-src*="youtube"], iframe[data-zf-src*="vimeo"]';
  function esc(u) { return String(u).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;'); }
  function playUrl(src) { return src + (src.indexOf('?') > -1 ? '&' : '?') + 'autoplay=1'; }
  function face(f) {
    if (f.hasAttribute('srcdoc') || !f.dataset.zfSrc) return;
    var src = f.dataset.zfSrc, m = src.match(/youtube(?:-nocookie)?\.com\/embed\/([\w-]{6,})/);
    var bg = m ? 'url(https://i.ytimg.com/vi/' + m[1] + '/hqdefault.jpg) center/cover no-repeat ' : '';
    var label = (f.getAttribute('title') || 'video');
    f.srcdoc = '<!doctype html><style>html,body{margin:0;height:100%;overflow:hidden;background:#000}a{position:absolute;inset:0;background:' + bg + '#000}' +
      'b{position:absolute;left:50%;top:50%;width:68px;height:48px;margin:-24px 0 0 -34px;border-radius:12px;background:rgba(0,0,0,.72)}' +
      'b:after{content:"";position:absolute;left:27px;top:14px;border-style:solid;border-width:10px 0 10px 17px;border-color:transparent transparent transparent #fff}' +
      'a:hover b,a:focus b{background:#f00}</style><a href="' + esc(playUrl(src)) + '" aria-label="Play ' + esc(label) + '"><b></b></a>';
  }
  function wake(f) {
    if (!f.dataset.zfSrc) return;
    var src = f.dataset.zfSrc; f.removeAttribute('data-zf-src'); f.removeAttribute('srcdoc'); f.src = src;
    if (io0) io0.unobserve(f); if (io1) io1.unobserve(f);
  }
  function watch(f) {
    face(f);
    if (!('IntersectionObserver' in window)) return wake(f);
    io0.observe(f);
    if (interacted) io1.observe(f);
  }
  function onIx() {
    if (interacted) return; interacted = true;
    [].forEach.call(document.querySelectorAll(SEL), function (f) { io1.observe(f); });
  }
  function init() {
    if (window.Shopify && Shopify.designMode) { [].forEach.call(document.querySelectorAll(SEL), wake); return; }
    if ('IntersectionObserver' in window) {
      var hit = function (es) { es.forEach(function (e) { if (e.isIntersecting) wake(e.target); }); };
      io0 = new IntersectionObserver(hit);
      io1 = new IntersectionObserver(hit, { rootMargin: '600px 0px' });
    }
    [].forEach.call(document.querySelectorAll(SEL), watch);
    ['scroll', 'pointerdown', 'keydown', 'touchstart', 'mousemove'].forEach(function (ev) { addEventListener(ev, onIx, { passive: true, once: true }); });
    if ('MutationObserver' in window) new MutationObserver(function (ms) {
      ms.forEach(function (m) { [].forEach.call(m.addedNodes, function (n) {
        if (n.nodeType !== 1) return;
        if (n.matches && n.matches(SEL)) watch(n);
        if (n.querySelectorAll) [].forEach.call(n.querySelectorAll(SEL), watch);
      }); });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', function () { if (window.Shopify && Shopify.designMode) [].forEach.call(document.querySelectorAll(SEL), wake); });
})();
